<script>
    import { formatTonnes, formatDuration, formatDateTime } from './reportHelpers.js';

    export let loads = [];
    export let selectedTruckId = 'all';
    export let availableTrucks = []; // list of regos present in loads
    export let searchTerm = '';
    export let reportTitle = '';

    // Filter loads by selected truck and search term
    $: filteredLoads = loads.filter(l => {
        if (selectedTruckId !== 'all' && l.rego.toLowerCase() !== selectedTruckId.toLowerCase()) {
            return false;
        }
        if (!searchTerm || !searchTerm.trim()) return true;
        const q = searchTerm.toLowerCase().trim();
        return (
            l.rego.toLowerCase().includes(q) ||
            l.driver.toLowerCase().includes(q) ||
            l.docket.toLowerCase().includes(q) ||
            l.jobNumber.toLowerCase().includes(q) ||
            l.customerName.toLowerCase().includes(q) ||
            l.materialName.toLowerCase().includes(q)
        );
    });

    // Single truck stats banner (if a specific truck is selected)
    $: singleTruckStats = (() => {
        if (selectedTruckId === 'all') return null;
        const truckLoads = loads.filter(l => l.rego.toLowerCase() === selectedTruckId.toLowerCase());
        if (truckLoads.length === 0) return null;

        const first = truckLoads[0];
        const cap = first.capacity || 10;
        const totalTonnes = truckLoads.length * cap;

        const tripMinsList = truckLoads.filter(l => l.tripMins != null).map(l => l.tripMins);
        const avgTrip = tripMinsList.length > 0
            ? Math.round(tripMinsList.reduce((a, b) => a + b, 0) / tripMinsList.length)
            : 10;

        const cycleMinsList = truckLoads.filter(l => l.cycleMins != null).map(l => l.cycleMins);
        const avgCycle = cycleMinsList.length > 0
            ? Math.round(cycleMinsList.reduce((a, b) => a + b, 0) / cycleMinsList.length)
            : 30;

        return {
            rego: first.rego,
            driver: first.driver,
            capacity: cap,
            loadsCount: truckLoads.length,
            totalTonnes,
            avgTrip,
            avgCycle
        };
    })();

    function selectTruck(rego) {
        selectedTruckId = rego;
    }
</script>

<div class="detail-view-container">
    <!-- Quick Truck Filter Chips -->
    <div class="truck-filter-bar no-print">
        <span class="bar-label">Filter Vehicle:</span>
        <button
            type="button"
            class="truck-chip-btn {selectedTruckId === 'all' ? 'active' : ''}"
            on:click={() => selectTruck('all')}
        >
            All Trucks ({loads.length} Loads)
        </button>
        {#each availableTrucks as t}
            {@const count = loads.filter(l => l.rego === t).length}
            <button
                type="button"
                class="truck-chip-btn {selectedTruckId === t ? 'active' : ''}"
                on:click={() => selectTruck(t)}
            >
                <span class="rego-inner">{t}</span>
                <span class="count-badge">{count}</span>
            </button>
        {/each}
    </div>

    <!-- Single Truck Info Card (if specific truck selected) -->
    {#if singleTruckStats}
        <div class="single-truck-hero">
            <div class="hero-left">
                <span class="hero-rego">{singleTruckStats.rego}</span>
                <div class="hero-info">
                    <span class="hero-driver">{singleTruckStats.driver}</span>
                    <span class="hero-sub">{singleTruckStats.capacity}t Payload Capacity</span>
                </div>
            </div>
            <div class="hero-kpis">
                <div class="hero-kpi-item">
                    <span class="hero-kpi-val text-primary">{singleTruckStats.loadsCount}</span>
                    <span class="hero-kpi-lbl">Loads Logged</span>
                </div>
                <div class="hero-kpi-item">
                    <span class="hero-kpi-val text-success">{formatTonnes(singleTruckStats.totalTonnes, 0)}</span>
                    <span class="hero-kpi-lbl">Tonnes Moved</span>
                </div>
                <div class="hero-kpi-item">
                    <span class="hero-kpi-val font-mono">{formatDuration(singleTruckStats.avgTrip)}</span>
                    <span class="hero-kpi-lbl">Avg Transit Time</span>
                </div>
                <div class="hero-kpi-item">
                    <span class="hero-kpi-val font-mono">{formatDuration(singleTruckStats.avgCycle)}</span>
                    <span class="hero-kpi-lbl">Avg Turnaround</span>
                </div>
            </div>
        </div>
    {/if}

    <!-- Main Loads Table Card -->
    <div class="table-card">
        <div class="table-card-header">
            <div>
                <h4 class="table-title">Individual Loads Log</h4>
                <p class="table-subtitle">Complete chronological record of haulage trips and dockets for {reportTitle}</p>
            </div>
            <div class="record-counter">
                {filteredLoads.length} {filteredLoads.length === 1 ? 'Load Recorded' : 'Loads Recorded'}
            </div>
        </div>

        {#if filteredLoads.length === 0}
            <div class="table-empty-state">
                <p>No loads found for the selected truck and criteria.</p>
            </div>
        {:else}
            <div class="table-responsive load-scroll-area">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th style="width: 60px;">Load #</th>
                            <th>Truck Rego</th>
                            <th>Driver</th>
                            <th>Job # & Customer</th>
                            <th>Material</th>
                            <th>Load Departure</th>
                            <th>Disposal Arrival</th>
                            <th class="text-right">Transit Time</th>
                            <th class="text-right">Cycle Turnaround</th>
                            <th>Docket / Ticket #</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each filteredLoads as l}
                            <tr class="data-row">
                                <td class="font-mono text-muted">#{l.loadIndex}</td>
                                <td>
                                    <span class="rego-chip">{l.rego}</span>
                                </td>
                                <td class="font-medium">{l.driver}</td>
                                <td>
                                    <div class="job-stack">
                                        <span class="job-badge">{l.jobNumber}</span>
                                        <span class="client-name">{l.customerName}</span>
                                    </div>
                                </td>
                                <td class="text-muted">{l.materialName}</td>
                                <td class="font-mono">{formatDateTime(l.loadTime)}</td>
                                <td class="font-mono">{l.unloadTime ? formatDateTime(l.unloadTime) : '-'}</td>
                                <td class="text-right font-mono font-medium">
                                    {l.tripMins != null ? formatDuration(l.tripMins) : (l.unloadTime ? '-' : 'En Route')}
                                </td>
                                <td class="text-right font-mono">
                                    {l.cycleMins != null ? formatDuration(l.cycleMins) : '-'}
                                </td>
                                <td>
                                    <span class="docket-chip">{l.docket}</span>
                                </td>
                                <td>
                                    {#if l.unloadTime}
                                        <span class="status-tip-clean">{l.issues}</span>
                                    {:else}
                                        <span class="status-en-route">In Transit</span>
                                    {/if}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
    </div>
</div>

<style>
    .detail-view-container {
        display: flex;
        flex-direction: column;
        gap: 1.15rem;
    }

    .truck-filter-bar {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
        background-color: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-radius: var(--tt-radius-md);
        padding: 8px 12px;
    }

    .bar-label {
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--tt-slate-500);
        margin-right: 4px;
    }

    .truck-chip-btn {
        background-color: var(--tt-slate-100);
        border: 1px solid var(--tt-slate-200);
        border-radius: var(--tt-radius-sm);
        padding: 4px 10px;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--tt-slate-700);
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;
        transition: all 0.15s ease;
    }

    .truck-chip-btn:hover {
        background-color: var(--tt-slate-200);
    }

    .truck-chip-btn.active {
        background-color: var(--tt-primary);
        border-color: var(--tt-primary);
        color: #ffffff;
    }

    .truck-chip-btn.active .rego-inner {
        background-color: #ffffff;
        color: #000000;
    }

    .truck-chip-btn.active .count-badge {
        background-color: rgba(255, 255, 255, 0.25);
        color: #ffffff;
    }

    .rego-inner {
        background-color: #fef08a;
        color: #000000;
        font-family: monospace;
        font-weight: 700;
        padding: 1px 5px;
        border-radius: 2px;
    }

    .count-badge {
        background-color: var(--tt-slate-200);
        color: var(--tt-slate-600);
        font-size: 0.6875rem;
        padding: 1px 5px;
        border-radius: 999px;
    }

    .single-truck-hero {
        background-color: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-left: 4px solid var(--tt-primary);
        border-radius: var(--tt-radius-md);
        padding: 1rem 1.25rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1.5rem;
        flex-wrap: wrap;
        box-shadow: 0 1px 2px rgba(0,0,0,0.03);
    }

    .hero-left {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .hero-rego {
        display: inline-block;
        background-color: #fef08a;
        color: #000000;
        font-family: monospace;
        font-size: 1.25rem;
        font-weight: 800;
        padding: 4px 10px;
        border-radius: 4px;
        border: 1px solid #eab308;
        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }

    .hero-info {
        display: flex;
        flex-direction: column;
    }

    .hero-driver {
        font-size: 1rem;
        font-weight: 700;
        color: var(--tt-slate-900);
    }

    .hero-sub {
        font-size: 0.75rem;
        color: var(--tt-slate-500);
    }

    .hero-kpis {
        display: flex;
        align-items: center;
        gap: 1.5rem;
    }

    .hero-kpi-item {
        display: flex;
        flex-direction: column;
        text-align: right;
    }

    .hero-kpi-val {
        font-size: 1.15rem;
        font-weight: 700;
        line-height: 1.2;
    }

    .hero-kpi-lbl {
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        color: var(--tt-slate-500);
    }

    .table-card {
        background-color: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-radius: var(--tt-radius-md);
        box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        overflow: hidden;
    }

    .table-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 1.25rem;
        background-color: var(--tt-slate-50);
        border-bottom: 1px solid var(--tt-slate-200);
    }

    .table-title {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 700;
        color: var(--tt-slate-900);
    }

    .table-subtitle {
        margin: 2px 0 0 0;
        font-size: 0.75rem;
        color: var(--tt-slate-500);
    }

    .record-counter {
        font-size: 0.75rem;
        font-weight: 600;
        background-color: var(--tt-slate-200);
        color: var(--tt-slate-700);
        padding: 3px 8px;
        border-radius: 999px;
    }

    .table-responsive {
        width: 100%;
        overflow-x: auto;
    }

    .load-scroll-area {
        max-height: 560px;
        overflow-y: auto;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
        text-align: left;
        font-size: 0.8125rem;
    }

    .data-table th {
        background-color: #ffffff;
        color: var(--tt-slate-600);
        font-size: 0.6875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 10px 12px;
        border-bottom: 1px solid var(--tt-slate-200);
        white-space: nowrap;
        position: sticky;
        top: 0;
        z-index: 10;
    }

    .data-table td {
        padding: 9px 12px;
        border-bottom: 1px solid var(--tt-slate-100);
        color: var(--tt-slate-800);
        vertical-align: middle;
    }

    .data-row:hover {
        background-color: var(--tt-slate-50);
    }

    .rego-chip {
        display: inline-block;
        background-color: #fef08a;
        color: #000000;
        font-family: monospace;
        font-weight: 700;
        padding: 2px 6px;
        border-radius: 3px;
        border: 1px solid #eab308;
    }

    .job-stack {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }

    .job-badge {
        font-family: monospace;
        font-weight: 600;
        color: var(--tt-slate-700);
    }

    .client-name {
        font-size: 0.75rem;
        color: var(--tt-slate-500);
    }

    .docket-chip {
        display: inline-block;
        font-family: monospace;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--tt-slate-800);
        background-color: var(--tt-slate-100);
        padding: 2px 6px;
        border-radius: 4px;
        border: 1px solid var(--tt-slate-200);
    }

    .status-tip-clean {
        font-size: 0.75rem;
        color: #15803d;
        font-weight: 500;
    }

    .status-en-route {
        font-size: 0.75rem;
        color: #b45309;
        font-weight: 600;
    }

    .text-right {
        text-align: right;
    }

    .text-muted {
        color: var(--tt-slate-500);
    }

    .text-primary {
        color: var(--tt-primary);
    }

    .text-success {
        color: #10b981;
    }

    .font-mono {
        font-family: monospace;
    }

    .font-medium {
        font-weight: 600;
    }

    .table-empty-state {
        padding: 3rem 1.5rem;
        text-align: center;
        color: var(--tt-slate-500);
        font-size: 0.875rem;
    }
</style>
